import type { Clip } from "@hudhud/core";
import { COLORS, HEIGHT } from "../types.js";
import { clampMap, interpolate } from "./interpolate.js";
import { spring } from "./spring.js";

/** Frames a full-screen opener keeps fading/sliding over the first frames of speech. */
export const OPENER_EXIT_FRAMES = 8;

/** The card backgrounds as radial gradients: centre (fraction of the frame) and colour stops (offset 0..1). */
export interface RadialBg { cx: number; cy: number; stops: { offset: number; color: string }[] }
export const CARD_BG: Record<Clip["hook"]["bg"], RadialBg> = {
  ink: { cx: 0.5, cy: 0.3, stops: [{ offset: 0, color: "#17211B" }, { offset: 0.7, color: COLORS.ink }] },
  green: { cx: 0.5, cy: 0.35, stops: [{ offset: 0, color: "#2A6B3F" }, { offset: 0.55, color: COLORS.green }, { offset: 1, color: "#0F2E1B" }] },
  gold: { cx: 0.5, cy: 0.3, stops: [{ offset: 0, color: "#FFD866" }, { offset: 0.6, color: COLORS.gold }, { offset: 1, color: "#C99A1E" }] },
  white: { cx: 0.5, cy: 0.3, stops: [{ offset: 0, color: "#FFFFFF" }, { offset: 0.7, color: COLORS.offWhite }, { offset: 1, color: "#E3DFD3" }] },
};
/** The same backgrounds as CSS. */
const pct = (v: number) => Math.round(v * 100); // 0.7 × 100 is 70.00000000000001 in floating point; the CSS says 70
export const cardBgCss = (bg: Clip["hook"]["bg"]): string => `radial-gradient(circle at ${pct(CARD_BG[bg].cx)}% ${pct(CARD_BG[bg].cy)}%, ${CARD_BG[bg].stops.map((s) => `${s.color} ${pct(s.offset)}%`).join(", ")})`;
export const FLAT_BG: Record<Clip["hook"]["bg"], string> = { green: COLORS.green, ink: COLORS.ink, gold: COLORS.gold, white: COLORS.offWhite };

export const titleSize = (clip: Pick<Clip, "hook">, base: number): number => {
  const len = clip.hook.text.replace(/\*/g, "").length;
  const factor = len > 70 ? 0.66 : len > 45 ? 0.8 : 1;
  return Math.round(base * factor * (clip.hook.fontSize / 100));
};
export const creditText = (clip: Pick<Clip, "hook">) => (clip.hook.showCredit ? clip.hook.credit.trim() : "");

export interface OpenerMotion {
  /** frames since the opener came in (60 when settled: past every entrance) */
  frame: number;
  enter: number;
  enterOpacity?: number; enterTy?: number; enterScale?: number;
  exit: number;
  exitTy?: number; exitOpacity?: number;
}
/** How far into the opener we are (`frame` already counts from when it came in), the entrance and the exit (0 = fully on, 1 = gone). */
export function openerMotion(clip: Pick<Clip, "hook">, frame: number, fps: number, exitAfterFrames: number, settled = false): OpenerMotion {
  const h = clip.hook;
  if (settled) return { frame: frame + 60, enter: 1, exit: 0 };
  const s = spring({ frame, fps, config: { damping: 14, stiffness: 150, mass: 0.8 } });
  const fadeIn = clampMap(frame, [0, 10], [0, 1]);
  const enter = h.animation === "none" ? 1 : h.animation === "fade" ? fadeIn : h.animation === "words" ? fadeIn : s;
  const out: OpenerMotion = { frame, enter, exit: clampMap(frame, [exitAfterFrames, exitAfterFrames + OPENER_EXIT_FRAMES], [0, 1]) };
  if (h.animation === "rise") { out.enterOpacity = s; out.enterTy = (1 - s) * 40; }
  else if (h.animation === "zoom") { out.enterOpacity = s; out.enterScale = 1.12 - 0.12 * s; }
  else if (h.animation === "fade") out.enterOpacity = fadeIn;
  if (h.transition === "slide") out.exitTy = -out.exit * HEIGHT;
  else if (h.transition === "fade") out.exitOpacity = 1 - out.exit;
  return out;
}

/** The "words" animation: each word pops in on its own, three frames apart. */
export const titleWordPop = (frame: number, idx: number, fps: number) => spring({ frame: frame - idx * 3, fps, config: { damping: 12, stiffness: 190 } });
/** An opener over the video fades over its last eight frames. */
export const overlayOpenerExit = (frame: number, total: number) => clampMap(frame, [total - 8, total], [1, 0]);
/** …and drops in from slightly above. */
export const overlayOpenerLift = (enter: number) => interpolate(enter, [0, 1], [-30, 0]);
